import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import API, { getMediaURL } from '../../../context/API';
import { useActiveStoreId } from '../../Stores/hooks';
import { requireActiveStoreId } from '../../../utils/activeStore';
import { Category, Customization, Item } from '../types';
import { QUERY_KEYS, DEFAULT_PICTURE } from '../constants';

interface ItemsPage {
  items: Item[];
  total: number;
}

async function fetchItemsPage(page: number, limit: number, search: string): Promise<ItemsPage> {
  const storeId = requireActiveStoreId();
  const response = await API.get(
    `/items/paginate?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}&storeId=${storeId}`
  );
  const items: Item[] = response.data.data.map((item: Item) => ({
    ...item,
    // The backend defaults unset photos to the "default.jpg" placeholder —
    // treat that as no picture rather than resolving it to a real CDN URL,
    // otherwise the edit form thinks a real photo was uploaded.
    picture: item.picture && item.picture !== DEFAULT_PICTURE ? getMediaURL(item.picture) ?? '' : '',
    // badgeImage was never being resolved to a full URL here, so any item
    // with a badge showed a broken image in the table and the edit drawer.
    badgeImage: item.badgeImage ? getMediaURL(item.badgeImage) ?? undefined : undefined,
  }));
  return { items, total: response.data.total };
}

export function useItemsQuery(search: string, page: number, pageSize: number) {
  const storeId = useActiveStoreId();

  return useQuery({
    queryKey: [QUERY_KEYS.ITEMS, search, page, pageSize, storeId],
    queryFn: () => fetchItemsPage(page, pageSize, search),
    staleTime: 2 * 60 * 1000,
    placeholderData: (prevData, prevQuery) =>
      prevQuery?.queryKey[4] === storeId ? prevData : undefined,
    enabled: !!storeId,
  });
}

export function useAllCustomizationsQuery(enabled = true) {
  const queryClient = useQueryClient();
  const storeId = useActiveStoreId();

  const query = useQuery({
    queryKey: [QUERY_KEYS.ALL_CUSTOMIZATIONS, storeId],
    queryFn: async (): Promise<Customization[]> => {
      const response = await API.get(`/customizations?storeId=${requireActiveStoreId()}`);
      return response.data.success ? response.data.data : [];
    },
    enabled: enabled && !!storeId,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    const refresh = () =>
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ALL_CUSTOMIZATIONS] });
    window.addEventListener('customizationUpdated', refresh);
    window.addEventListener('customizationCreated', refresh);
    return () => {
      window.removeEventListener('customizationUpdated', refresh);
      window.removeEventListener('customizationCreated', refresh);
    };
  }, [queryClient]);

  return query;
}

export function useAllCategoriesQuery(enabled = true) {
  const storeId = useActiveStoreId();

  return useQuery({
    queryKey: [QUERY_KEYS.ALL_CATEGORIES, storeId],
    queryFn: async (): Promise<Category[]> => {
      const response = await API.get(`/categories?storeId=${requireActiveStoreId()}`);
      return response.data.success ? response.data.data : [];
    },
    enabled: enabled && !!storeId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useItemCustomizationsQuery(itemId: number | string | null) {
  const storeId = useActiveStoreId();

  return useQuery({
    queryKey: [QUERY_KEYS.ITEM_CUSTOMIZATIONS, itemId, storeId],
    queryFn: async (): Promise<Customization[]> => {
      const res = await API.get(`/items/${itemId}/customizations?storeId=${requireActiveStoreId()}`);
      if (!res.data.success) return [];
      return res.data.data.customizations
        .map((c: any) => ({ ...c.customization, sequence_number: c.sequence_number }))
        .sort(
          (a: Customization, b: Customization) =>
            parseInt(a.sequence_number as string) - parseInt(b.sequence_number as string)
        );
    },
    enabled: !!itemId && !!storeId,
  });
}

export function useItemCategoriesQuery(itemId: number | string | null) {
  const storeId = useActiveStoreId();

  return useQuery({
    queryKey: [QUERY_KEYS.ITEM_CATEGORIES, itemId, storeId],
    queryFn: async (): Promise<Category[]> => {
      const res = await API.get(`/items/${itemId}/categories?storeId=${requireActiveStoreId()}`);
      return res.data.success ? res.data.data.category_names : [];
    },
    enabled: !!itemId && !!storeId,
  });
}

export function useItemsStatsQuery() {
  const storeId = useActiveStoreId();

  return useQuery({
    queryKey: ['items-stats', storeId],
    queryFn: async (): Promise<{ total: number; soldOutCount: number }> => {
      const res = await API.get(`/items/paginate?page=1&limit=1&storeId=${requireActiveStoreId()}`);
      const total: number = res.data.total || 0;
      // Approximate sold out count from the paginated list if available
      const soldOutCount: number = res.data.soldOutCount ?? 0;
      return { total, soldOutCount };
    },
    enabled: !!storeId,
    staleTime: 2 * 60 * 1000,
  });
}
